import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { IntegrationCard } from './IntegrationCard';

const CONNECTOR_ID = 'hubspot';

interface HubSpotRecord {
  id: string;
  properties: Record<string, string | undefined>;
}

type RecordType = 'contacts' | 'companies' | 'deals';

const LABELS: Record<RecordType, string> = {
  contacts: 'Contacts',
  companies: 'Companies',
  deals: 'Deals',
};

/** Waits for the OAuth popup to report success on our own origin. */
function waitForOAuthCompletion(popup: Window) {
  return new Promise<void>((resolve, reject) => {
    let poll: number | undefined;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      if (poll !== undefined) window.clearInterval(poll);
    };
    const onMessage = (event: MessageEvent) => {
      const type = event.data?.type;
      if (
        event.origin !== window.location.origin ||
        event.data?.connectorId !== CONNECTOR_ID ||
        (type !== 'appUserConnectorOAuthComplete' && type !== 'appUserConnectorOAuthFailed')
      ) return;
      cleanup();
      if (type === 'appUserConnectorOAuthComplete') return resolve();
      popup.close();
      reject(new Error(event.data?.reason ?? 'HubSpot connection failed.'));
    };
    window.addEventListener('message', onMessage);
    poll = window.setInterval(() => {
      if (!popup.closed) return;
      cleanup();
      reject(new Error('The HubSpot window was closed before finishing.'));
    }, 500);
  });
}

export const HubSpotOAuthIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recordType, setRecordType] = useState<RecordType>('contacts');
  const [records, setRecords] = useState<HubSpotRecord[]>([]);

  const refreshStatus = useCallback(async () => {
    setChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('hubspot-user-data', {
        body: { action: 'status' },
      });
      if (error) throw error;
      setIsConnected(Boolean(data?.connected));
      if (data && data.success === false && data.error) {
        toast.error(data.error);
      }
    } catch (e: any) {
      console.error('HubSpot status check failed:', e);
      setIsConnected(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { void refreshStatus(); }, [refreshStatus]);

  const handleConnect = async () => {
    // Pre-open inside the click so the popup is not blocked after the round-trip.
    const popup = window.open('', 'hubspot-oauth', 'width=600,height=760');
    if (!popup) {
      toast.error('Please allow pop-ups for this site and try again.');
      return;
    }
    setIsConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('hubspot-connect-start', {
        body: { origin: window.location.origin },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.authorizationUrl) throw new Error('HubSpot did not return a sign-in link.');

      const completion = waitForOAuthCompletion(popup);
      popup.location.href = data.authorizationUrl;
      await completion;

      toast.success('HubSpot connected to your account.');
      await refreshStatus();
    } catch (e: any) {
      popup.close();
      console.error('HubSpot connect error:', e);
      toast.error(e?.message || 'Could not connect HubSpot.');
    } finally {
      setIsConnecting(false);
    }
  };

  const loadRecords = async (type: RecordType) => {
    setIsLoading(true);
    setRecordType(type);
    try {
      const { data, error } = await supabase.functions.invoke('hubspot-user-data', {
        body: { action: type },
      });
      if (error) throw error;
      if (!data?.success) {
        setIsConnected(Boolean(data?.connected));
        toast.error(data?.error || `Could not load ${LABELS[type].toLowerCase()}.`);
        return;
      }
      setRecords(data.data || []);
      setIsConnected(true);
      toast.success(`Loaded ${data.count} ${LABELS[type].toLowerCase()} from your HubSpot.`);
    } catch (e: any) {
      console.error('HubSpot load error:', e);
      toast.error(e?.message || 'Could not load HubSpot records.');
    } finally {
      setIsLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('hubspot-user-data', {
        body: { action: 'disconnect' },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error);
      setIsConnected(false);
      setRecords([]);
      toast.success('HubSpot disconnected.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not disconnect HubSpot.');
    }
  };

  const primaryLine = (r: HubSpotRecord) => {
    const p = r.properties;
    if (recordType === 'contacts') {
      return [p.firstname, p.lastname].filter(Boolean).join(' ') || p.email || r.id;
    }
    if (recordType === 'companies') return p.name || p.domain || r.id;
    return p.dealname || r.id;
  };

  const secondaryLine = (r: HubSpotRecord) => {
    const p = r.properties;
    if (recordType === 'contacts') return [p.email, p.company].filter(Boolean).join(' · ');
    if (recordType === 'companies') return [p.domain, p.industry].filter(Boolean).join(' · ');
    return [p.amount, p.dealstage].filter(Boolean).join(' · ');
  };

  return (
    <IntegrationCard
      icon={
        <div className="w-6 h-6 bg-orange-500 rounded-sm flex items-center justify-center">
          <span className="text-white text-xs font-bold">H</span>
        </div>
      }
      name="HubSpot"
      description="Sign in with your own HubSpot account. Veylo reads only your contacts, companies and deals — nothing is shared with other users of the app."
      isConnected={isConnected}
      checking={checking}
      isConnecting={isConnecting}
      onConnect={handleConnect}
      onDisconnect={disconnect}
      actions={(Object.keys(LABELS) as RecordType[]).map((t) => (
        <Button key={t} onClick={() => loadRecords(t)} disabled={isLoading} variant="outline">
          {isLoading && recordType === t ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</>
          ) : (
            `Load ${LABELS[t]}`
          )}
        </Button>
      ))}
    >
      {records.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">{LABELS[recordType]} ({records.length})</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {records.map((r) => (
              <div key={r.id} className="p-3 border rounded-lg">
                <div className="font-medium">{primaryLine(r)}</div>
                <div className="text-sm text-muted-foreground">{secondaryLine(r)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </IntegrationCard>
  );
};
